import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatCircleTextIcon } from '@phosphor-icons/react/dist/csr/ChatCircleText';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { justificationsApi, type JustificationRow } from '../../lib/api/justifications';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './AnswerDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * `POST /justifications/{justification}/answer` (M-11, Task 4, spec §2, J-3,
 * J-5).
 *
 * ## A `403` here means "you are not this complaint's own subject" (spec J-3)
 *
 * `JustificationDetail.tsx`'s own docblock explains why the Answer button
 * is offered on permission and status alone, never a client-side
 * `subject.uuid === me` check: the server is the one authority on whether
 * THIS caller may answer THIS row. A `403` therefore surfaces here through
 * the ordinary `describeFailure(FORBIDDEN_CODE)` path, worded plainly
 * rather than assumed away — the identical shape M-09's `LogCallDialog.tsx`
 * uses for its own forbidden case.
 *
 * ## The server's refusal is not always keyed to `justification`
 *
 * `justification` is the one field this form renders. A `409` for
 * "already answered" or "already approved" (spec J-4, J-5) names `status`,
 * never `justification` — so it goes to the failure banner rather than
 * being silently dropped, the identical split `EscalationActionDialog.tsx`'s
 * own docblock explains at length for its `notes`-vs-`status`/`level` case.
 * The fixture this dialog's own test suite exercises for that path carries
 * a REALISTIC keyed `details: { status: [...] }`, never an empty `{}` that
 * would only prove the generic fallback runs (task brief's fifth defect).
 */

export interface AnswerDialogProps {
  open: boolean;
  /** `null` while nothing is being acted on — this dialog is not mounted with `open` in that state, but the type still allows it so a caller cannot pass a stale uuid by construction. */
  justificationUuid: string | null;
  /** The complaint's own subject name, so the dialog's title names the record rather than asking a bare "Are you sure?" (`Dialog`'s own development guard). */
  subjectName: string;
  onClose: () => void;
  onDone: (updated: JustificationRow) => void;
}

export function AnswerDialog({ open, justificationUuid, subjectName, onClose, onDone }: AnswerDialogProps) {
  const { t } = useTranslation();

  const [text, setText] = useState('');
  const [missingText, setMissingText] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setText('');
    setMissingText(false);
    setFailure(null);
    setFieldErrors({});
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('justifications.notPermittedToAnswer');
      case TOO_MANY_REQUESTS_CODE:
        return t('justifications.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('justifications.answerFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || justificationUuid === null) return;

    if (text.trim() === '') {
      setMissingText(true);
      return;
    }
    setMissingText(false);

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const updated = await justificationsApi.answer(justificationUuid, { justification: text.trim() });
      onDone(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);

      // THE SERVER'S REFUSAL IS NOT ALWAYS KEYED TO `justification` — see
      // this file's own docblock. `status` (already answered/approved) is
      // neither, so it surfaces in the banner instead of being dropped.
      const banner = Object.entries(mapped)
        .filter(([field]) => field !== 'justification')
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t('justifications.answerTitle', { subject: subjectName })}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Textarea
          label={t('justifications.answerLabel')}
          dir="auto"
          value={text}
          error={missingText ? t('justifications.required') : fieldErrors['justification']}
          onChange={(event) => {
            const { value } = event.target;
            setText(value);
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<ChatCircleTextIcon size={16} />}>
            {t('justifications.answerSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('justifications.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
